import type {Auth0ContextInterface} from "@auth0/auth0-react";
import type {Location, NavigateFunction} from "react-router";
import type {AuthManager} from "../AuthManager";
import axios from "axios";
import type {MithraLoginResponse} from "../../services/ApiTypes";
import {environment} from "../../env";
import {B} from "../../build";
import type AuthStore from "../AuthStore";
import type Rollbar from "rollbar";
import {LOGIN_STATE_SESSION_KEY, auth0RedirectUriForPath, type LoginRouteState} from "../routing/LoginRouteState";

export type Auth0CallbackError = {
    code: string;
    message: string;
};

function formatAuth0CallbackErrorMessage(code: string, description: string | null): string {
    if (code === 'access_denied') {
        return 'Login was cancelled';
    }
    if (description) {
        try {
            return decodeURIComponent(description.replace(/\+/g, ' '));
        } catch {
            return description;
        }
    }
    return 'Login failed, please try again';
}

/** Auth0 returns ?error=...&error_description=... when the user declines or login fails. */
export function getAuth0CallbackSearchError(search: string): Auth0CallbackError | null {
    const params = new URLSearchParams(search);
    const code = params.get('error');
    if (!code) {
        return null;
    }
    return {
        code,
        message: formatAuth0CallbackErrorMessage(code, params.get('error_description')),
    };
}

export function startAuth0Login(
    authStore: AuthStore,
    auth0Context: Auth0ContextInterface,
    loginState: LoginRouteState | null,
    rollbar?: Rollbar,
): void {
    const authManager: AuthManager = authStore.authentication;

    authManager.setAlert(null);
    authManager.startLogin('auth0');

    if (loginState) {
        sessionStorage.setItem(LOGIN_STATE_SESSION_KEY, JSON.stringify(loginState));
    } else {
        sessionStorage.removeItem(LOGIN_STATE_SESSION_KEY);
    }

    const returnTo = loginState?.loginIntention.onSuccessRedirectTo
        ?? `${window.location.pathname}${window.location.search}${window.location.hash}`;

    auth0Context.loginWithRedirect({
        authorizationParams: {
            organization: B.AUTH0_ORGANIZATION,
            returnTo: auth0RedirectUriForPath(returnTo),
        },
    }).catch(err => {
        (rollbar ?? console).error('auth0Login: loginWithRedirect(): failed', err);
        authManager.processMithraLoginError(err);
    });
}

export function completeAuth0MithraLogin(
    authStore: AuthStore,
    auth0Context: Auth0ContextInterface,
    location: Location,
    navigate: NavigateFunction,
    rollbar?: Rollbar,
): Promise<void> {
    const authManager: AuthManager = authStore.authentication;

    authManager.setIsMithraLoggingIn('auth0');
    return auth0Context.getAccessTokenSilently()
        .then(access_token => {
            return axios.post<MithraLoginResponse>(`${environment.authUrl}/login/auth0/`, {access_token})
                .then(resp => {
                    (rollbar ?? console).debug('auth0Login: POST login/auth0 succeeded', resp.data?.user?.email);
                    authManager.processMithraLoginSuccess(resp.data, location, navigate, 'auth0', rollbar);
                })
                .catch(err => {
                    (rollbar ?? console).error('auth0Login: POST login/auth0: failed', err);
                    authManager.processMithraLoginError(err);
                    throw err;
                });
        })
        .catch(err => {
            if (!authManager.isMithraLoggingIn) {
                return;
            }
            (rollbar ?? console).error('auth0Login: getAccessTokenSilently(): failed', err);
            authManager.processMithraLoginError(err);
            throw err;
        });
}
